import * as React from 'react'
import { Button, ButtonGroup } from '../../Button'
import { styled } from '../../../theme/styled'
import { EditIcon } from '../../icons/EditIcon'
import { RadiusIcon } from '../../icons/RadiusIcon'

interface TopActionsProps {
  after?: React.ReactNode
  shapeEditor?: 'circle' | 'polygon'
  onCancelClick?: () => void
  onApplyClick?: () => void
  onRadiusClick?: () => void
  onDrawClick?: () => void
  onClearClick?: () => void
}

export const TopActions = ({
  after,
  shapeEditor,
  onCancelClick,
  onApplyClick,
  onRadiusClick,
  onDrawClick,
  onClearClick
}: TopActionsProps) => {
  const buttons: Array<{
    label: string
    icon?: JSX.Element
    onClick?: () => void
    visible: boolean
    active?: boolean
  }> = React.useMemo(
    () => [
      {
        label: 'Draw',
        icon: <EditIcon />,
        onClick: onDrawClick,
        visible: !!onDrawClick,
        active: shapeEditor && shapeEditor === 'polygon'
      },
      {
        label: 'Radius',
        icon: <RadiusIcon />,
        onClick: onRadiusClick,
        visible: !!onRadiusClick,
        active: shapeEditor && shapeEditor === 'circle'
      },
      {
        label: 'Clear',
        onClick: onClearClick,
        visible: !!onClearClick
      },
      {
        label: 'Cancel',
        onClick: onCancelClick,
        visible: !!onCancelClick
      },
      {
        label: 'Apply',
        onClick: onApplyClick,
        visible: !!onApplyClick
      }
    ],
    [shapeEditor, onCancelClick, onApplyClick, onRadiusClick, onDrawClick, onClearClick]
  )

  return (
    <TopActionsContainer>
      <ActionButtons>
        {buttons.map(
          (button) =>
            button.visible && (
              <StyledButton
                key={button.label}
                onClick={button.onClick}
                $active={button.active || undefined}
                $hasIcon={Boolean(button.icon)}
              >
                {button.icon && <IconWrapper>{button.icon}</IconWrapper>}
                <span>{button.label}</span>
              </StyledButton>
            )
        )}
      </ActionButtons>
      {after}
    </TopActionsContainer>
  )
}

const ActionButtons = styled(ButtonGroup)`
  background: ${(p) => p.theme.av8.background};
`

const StyledButton = styled(Button)<{ $active?: boolean; $hasIcon?: boolean }>`
  border: 0 !important;
  padding: 0.75rem ${(p) => (p.$hasIcon ? '0.5rem' : '1rem')};
  ${(p) =>
    p.$active &&
    `
    &, &:active, &:focus, &:hover{
      background: ${p.theme.av8.primaryColor}!important;
      color: ${p.theme.av8.background};
    }
    border: 0 !important;
    z-index: 2;
  `}
`

const TopActionsContainer = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  width: 100%;
  pointer-events: none;
  & > * {
    pointer-events: all;
    margin: 0.5rem;
    margin-bottom: 0;
  }
`

const IconWrapper = styled.div`
  margin-right: 0.5rem;
  line-height: 0;
`
